import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps } from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap font-semibold text-sm outline-none transition-[color,background-color] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:bg-gray-100 disabled:text-gray-400 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-gray-800 active:bg-gray-600 disabled:bg-gray-100 disabled:text-gray-400",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive-foreground/10 focus-visible:ring-destructive/60 active:bg-destructive-foreground/15 disabled:opacity-50 dark:focus-visible:ring-destructive/40",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-gray-200 active:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400",
        ghost:
          "text-primary hover:bg-gray-50 active:bg-transparent disabled:bg-transparent",
        link: "text-primary underline-offset-4 hover:underline active:text-primary/80 disabled:bg-transparent",
        inForm:
          "bg-secondary text-secondary-foreground hover:bg-gray-100 active:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-500",
        inFormSecondary:
          "bg-white text-secondary-foreground hover:bg-gray-100 active:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-500 dark:bg-black",
      },
      size: {
        default: "h-12 rounded-button px-5",
        xs: "h-8 rounded-button-xs px-3",
        sm: "h-10 rounded-button-sm px-4",
        lg: "h-17 rounded-button-lg px-8",
        icon: "size-12 rounded-button",
        iconXs: "size-8 rounded-button-xs",
        iconSm: "size-10 rounded-button-sm",
        iconLg: "size-17 rounded-button-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      data-slot="button"
      {...(!asChild && { type: "button" })}
      {...props}
    />
  );
}

export { Button, buttonVariants };
